/* eslint-disable eqeqeq */
import React, { useRef, useState, useMemo, useEffect } from 'react';
import * as yup from 'yup';
import { FormDialog } from 'components/dialogs/formDialog';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DropdownWrapper } from 'components/formInputs/wrappers/dropdownWrapper';
import { SelectedItemButton } from 'components/buttons/selectedItemButton';
import { getTranslatedData } from 'locales';
import { TextboxTypeWrapper } from 'components/formInputs/wrappers/textboxTypeWrapper';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';

const types = { FIXED: 'Fixed', PERCENTAGE: 'Percent' };

export const CouponProductAddEditForm = ({
  itemData,
  isOpen,
  closeModal,
  onSaved,
  allProducts,
  allCategories,
}) => {
  const cancelButtonRef = useRef(null);
  const formRef = useRef(null);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [selectedType, setSelectedType] = useState(types.PERCENTAGE);

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),

    discountValue: yup
      .number()
      .min(0, 'Discount must be greater than 0')
      .typeError('Please enter a valid number.')
      .nullable(false)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : -1))
      .required('Please enter a discount value.')
      .default(itemData?.use_fixed ? itemData?.fixed : itemData?.percent),

    min_quantity: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.min_quantity),
    apply_on_quantity: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.apply_on_quantity),

    can_repeat_quantity_apply: yup.boolean().default(itemData?.can_repeat_quantity_apply || false),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setValue,
    setError,
    reset,
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (itemData) {
      setSelectedProducts(itemData.products || []);
      setSelectedCategories(itemData.categories || []);
      setSelectedType(itemData?.use_fixed ? types.FIXED : types.PERCENTAGE);
      reset(itemData);
    }
  }, [itemData, reset]);

  const onSubmitClicked = () => {
    formRef.current.click();
  };

  const onSubmit = async (data) => {
    const submitData = {
      categories: data.categories || [],
      products: data.products || [],
      use_fixed: selectedType === types.FIXED,
      percent: selectedType === types.PERCENTAGE ? data.discountValue : 0,
      fixed: selectedType === types.FIXED ? data.discountValue : 0,
      min_quantity: data.min_quantity,
      apply_on_quantity: data.apply_on_quantity,
      can_repeat_quantity_apply: data.can_repeat_quantity_apply,
    };

    if (submitData.categories.length === 0 && submitData.products.length === 0) {
      setError('categories', { message: 'Select at least 1 category or product' });
      return;
    }

    onSaved(submitData);
  };
  const onError = (data) => console.log(data);

  const getCategoriesDataSource = () => {
    const filteredCategories = allCategories?.filter((c) => !selectedCategories.includes(c._id));
    const options = filteredCategories?.map((x) => ({
      label: getTranslatedData(x.name),
      value: x._id,
    }));

    options?.unshift({
      label: selectedCategories && selectedCategories.length > 0 ? '-' : 'All',
      value: '-1',
    });

    return options;
  };

  const getProductsDataSource = () => {
    const filteredProducts = allProducts?.filter((c) => !selectedProducts.includes(c._id));
    const options = filteredProducts?.map((x) => ({
      label: getTranslatedData(x.name),
      value: x._id,
    }));

    options?.unshift({
      label: selectedProducts && selectedProducts.length > 0 ? '-' : 'All',
      value: '-1',
    });

    return options;
  };

  const renderSelectedCategories = () => {
    if (!selectedCategories) return null;

    return (
      <ul className="mt-1">
        {selectedCategories.map((selected) => {
          const category = allCategories?.find(
            (x) => x._id === selected,
          );

          if (!category) return null;
          return (
            <div key={category._id}>
              <SelectedItemButton
                name={getTranslatedData(category.name)}
                onClick={() => {
                  const newCategories = selectedCategories.filter((x) => x !== category._id);

                  setSelectedCategories(newCategories);
                  setValue('categories', newCategories);
                }}
              />
            </div>
          );
        })}
      </ul>
    );
  };

  const renderSelectedProducts = () => {
    if (!selectedProducts) return null;

    return (
      <ul className="mt-1">
        {selectedProducts.map((selected) => {
          const product = allProducts?.find(
            (x) => x._id === selected,
          );

          if (!product) return null;
          return (
            <div key={product._id}>
              <SelectedItemButton
                name={getTranslatedData(product.name)}
                onClick={() => {
                  const newProducts = selectedProducts.filter((x) => x !== product._id);

                  setSelectedProducts(newProducts);
                  setValue('products', newProducts);
                }}
              />
            </div>
          );
        })}
      </ul>
    );
  };

  return (
    <FormDialog
      isOpen={isOpen}
      cancelButtonRef={cancelButtonRef}
      onClose={closeModal}
      title={itemData ? 'Edit' : 'Add New'}
      buttons={[
        { text: 'Save', onClick: onSubmitClicked, color: 'blue' },
        { text: 'Close', onClick: closeModal, color: 'gray' },
      ]}
    >
      <div>

        <form onSubmit={handleSubmit(onSubmit, onError)} method="POST">
          <div className="grid grid-cols-6 gap-6">

            <div className="col-span-6 sm:col-span-3">
              <DropdownWrapper
                name="categories"
                title="Categories"
                options={getCategoriesDataSource()}
                error={errors?.categories}
                onChange={(e) => {
                  setSelectedCategories((oldArray) => [...oldArray, e.target.value]);
                  setValue('categories', [...selectedCategories, e.target.value]);
                  clearErrors('categories');
                }}
                renderSelections={renderSelectedCategories}
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <DropdownWrapper
                name="products"
                title="Products"
                options={getProductsDataSource()}
                error={errors?.products}
                onChange={(e) => {
                  setSelectedProducts((oldArray) => [...oldArray, e.target.value]);
                  setValue('products', [...selectedProducts, e.target.value]);
                  clearErrors('categories');
                }}
                renderSelections={renderSelectedProducts}
              />
            </div>

            <div className="col-span-6 md:col-span-6 lg:col-span-3">
              <TextboxTypeWrapper
                register={register}
                title="Amount*"
                name="discountValue"
                error={errors?.discountValue}
                options={[
                  { label: 'Percentage', value: types.PERCENTAGE },
                  { label: 'Fixed', value: types.FIXED },
                ]}
                onChangeSelectedType={setSelectedType}
                selectedType={selectedType}
              />
            </div>

            <div className="hidden lg:block lg:col-span-3" />
            <div className="col-span-6 md:col-span-6 lg:col-span-3">
              <TextboxWrapper
                register={register}
                name="min_quantity"
                title="Minimum Quantity"
                subtitle="required to reach selected items quantity for this coupon to apply (leave empty to apply to any amount)"
                error={errors?.min_quantity}
              />
            </div>

            <div className="col-span-6 md:col-span-6 lg:col-span-3">
              <TextboxWrapper
                register={register}
                name="apply_on_quantity"
                title="Apply on Quantity"
                subtitle="once minimum quantity is reached, applies discount on this amount of items (leave empty to apply to all)"
                error={errors?.apply_on_quantity}
              />
            </div>

            <div className="col-span-6 md:col-span-6 lg:col-span-3">
              <CheckboxWrapper
                register={register}
                name="can_repeat_quantity_apply"
                title="Repeat Quantity Apply"
                subtitle="discount can be applied multiple times"
              />
            </div>

          </div>
          <input id="hidden_submit_button" type="submit" style={{ display: 'none' }} ref={formRef} />
        </form>
      </div>
    </FormDialog>
  );
};
